<template>
    <div
        v-gtm:type="'Standings'"
        v-gtm:@view
        class="section"
    >
        <Heading> {{ i18n.t('tournament_center_title_standings') }} </Heading>
        <div class="tabBar">
            <SegmentedControl
                v-model="selectedMode"
                v-bind:items="modes"
                v-bind:color="'var(--white)'"
                v-bind:activeColor="'var(--white)'"
            />
            <Navbar
                v-if="currentTab.length"
                v-bind:isShowBottomRow="false"
                v-bind:isTransparent="true"
            >
                <template #topRow>
                    <NavbarMain>
                        <NavbarItem
                            v-for="item in currentTab"
                            v-bind:key="item.key"
                            v-gtm:label="item.gtmLabel"
                            v-gtm:@click
                            v-bind:name="item.name"
                            v-bind:isActive="item.isActive"
                            v-bind:onclick="item.onClick"
                            v-bind:hasBottomRow="false"
                            v-bind:isActiveHasUnderline="true"
                        />
                    </NavbarMain>
                </template>
            </Navbar>
        </div>
        <Groups
            v-if="selectedMode === TournamentStandingTab.Groups"
            v-bind:groups="filteredGroups"
            v-bind:loaded="groupsLoaded"
        />
        <Knockout
            v-else
            v-bind:knockoutStages="orderedStages"
            v-bind:events="events"
            v-bind:selectedStage="selectedKnockoutStage"
            v-bind:loaded="knockoutLoaded"
            v-on:update:selectedKnockoutStage="updateSelectedKnockoutStage($event)"
        />
    </div>
</template>

<script lang="ts" setup>
    import sortBy from 'lodash/sortBy';
    import { storeToRefs } from 'pinia';
    import { computed, ref, toRefs, watch } from 'vue';
    import Navbar from '@/components/common/navbar/Navbar.vue';
    import NavbarItem from '@/components/common/navbar/NavbarItem.vue';
    import NavbarMain from '@/components/common/navbar/NavbarMain.vue';
    import SegmentedControl from '@/components/common/segmentedControl/SegmentedControl.vue';
    import Groups from '@/components/tournamentCenter/Groups.vue';
    import Heading from '@/components/tournamentCenter/Heading.vue';
    import Knockout from '@/components/tournamentCenter/Knockout.vue';
    import { getKnockoutCurrentStage, getKnockoutStagesForSlides } from '@/components/tournamentCenter/tournamentCenterHelper';
    import { i18n } from '@/core/lib/i18n';
    import { useTournamentGroups } from '@/core/oddsApi/composable/useTournamentGroups';
    import { useTournamentKnockout } from '@/core/oddsApi/composable/useTournamentKnockout';
    import { SiteStyle, TournamentStage, TournamentStandingTab } from '@/interface/enum';
    import type { ITournamentEvent } from '@/interface/tournamentCenter';
    import { useToggleStore } from '@/store/toggleStore';

    enum GroupTab {
        AF = 'AF',
        GL = 'GL',
    }

    const props = defineProps<{
        leagueId: number;
        events: ITournamentEvent[];
    }>();

    const { leagueId } = toRefs(props);

    const { groups, loaded: groupsLoaded } = useTournamentGroups(leagueId.value);

    const { tournamentCenterStageStartDateByLeagueConfig } = storeToRefs(useToggleStore(SiteStyle.Asia));

    const currentStageByLeagueId = computed(() => getKnockoutCurrentStage(tournamentCenterStageStartDateByLeagueConfig.value, props.leagueId));

    const isInKnockoutStage = computed(() => {
        const stage = currentStageByLeagueId.value;
        return stage !== TournamentStage.Unknown && stage !== TournamentStage.Group;
    });

    const selectedMode = ref<TournamentStandingTab>(isInKnockoutStage.value ? TournamentStandingTab.Knockouts : TournamentStandingTab.Groups);

    const modes = computed(() => [
        { value: TournamentStandingTab.Groups, title: i18n.t('tournament_center_groups'), gtmLabel: 'Groups_Tab' },
        { value: TournamentStandingTab.Knockouts, title: i18n.t('tournament_center_knockouts'), gtmLabel: 'Knockouts_Tab' },
    ]);

    const selectedGroupTab = ref(GroupTab.AF);
    const selectedKnockoutStage = ref(TournamentStage.Unknown);

    const { knockoutStages, loaded: knockoutLoaded } = useTournamentKnockout(leagueId);
    const knockoutStageMapping = computed<Record<TournamentStage, Record<string, string>>>(() => ({
        [TournamentStage.Unknown]: {
            title: '',
            gtmLabel: '',
        },
        [TournamentStage.RoundOf32]: {
            title: i18n.t('tournament_center_round_of_32'),
            gtmLabel: 'Knockouts_32',
        },
        [TournamentStage.RoundOf16]: {
            title: i18n.t('tournament_center_round_of_16'),
            gtmLabel: 'Knockouts_16',
        },
        [TournamentStage.QuarterFinals]: {
            title: i18n.t('tournament_center_round_of_8'),
            gtmLabel: 'Knockouts_8',
        },
        [TournamentStage.SemiFinals]: {
            title: i18n.t('tournament_center_round_of_4'),
            gtmLabel: 'Knockouts_4',
        },
        [TournamentStage.Final]: {
            title: i18n.t('tournament_center_final_round'),
            gtmLabel: 'Knockouts_2',
        },
        [TournamentStage.ThirdPlace]: {
            title: '',
            gtmLabel: '',
        },
        [TournamentStage.Group]: {
            title: '',
            gtmLabel: '',
        },
    }));

    const KNOCKOUT_SLIDE_STAGE_ORDER = [
        TournamentStage.RoundOf32,
        TournamentStage.RoundOf16,
        TournamentStage.QuarterFinals,
        TournamentStage.SemiFinals,
        TournamentStage.ThirdPlace,
        TournamentStage.Final,
    ];
    const orderedStages = computed(() => sortBy(knockoutStages.value, [s => KNOCKOUT_SLIDE_STAGE_ORDER.indexOf(s.stage)]));
    const knockoutStagesTabs = computed(() => getKnockoutStagesForSlides(orderedStages.value).map(stage => ({
        key: stage,
        name: knockoutStageMapping.value[stage].title,
        isActive: selectedKnockoutStage.value === stage,
        gtmLabel: knockoutStageMapping.value[stage].gtmLabel,
        onClick: () => {
            selectedKnockoutStage.value = stage;
        },
    })));

    const tabs = computed(() => ({
        [TournamentStandingTab.Groups]: [
            {
                key: GroupTab.AF,
                gtmLabel: 'GroupA-F',
                name: i18n.t('tournament_center_group_to_group', { group_from: 'A', group_to: 'F' }),
                isActive: selectedGroupTab.value === GroupTab.AF,
                onClick: () => {
                    selectedGroupTab.value = GroupTab.AF;
                },
            },
            {
                key: GroupTab.GL,
                gtmLabel: 'GroupG-L',
                name: i18n.t('tournament_center_group_to_group', { group_from: 'G', group_to: 'L' }),
                isActive: selectedGroupTab.value === GroupTab.GL,
                onClick: () => {
                    selectedGroupTab.value = GroupTab.GL;
                },
            },
        ],
        [TournamentStandingTab.Knockouts]: knockoutStagesTabs.value,
    }));

    const currentTab = computed(() => tabs.value[selectedMode.value]);
    const GROUPS_PER_SECTION = 6;
    const orderedGroups = computed(() => [...groups.value].sort((a, b) => a.groupName.localeCompare(b.groupName, 'en', { sensitivity: 'base' })));
    const filteredGroups = computed(() => (selectedGroupTab.value === GroupTab.AF
        ? orderedGroups.value.slice(0, GROUPS_PER_SECTION)
        : orderedGroups.value.slice(GROUPS_PER_SECTION)));

    watch(knockoutLoaded, () => {
        if (knockoutLoaded.value) {
            if (![TournamentStage.Group, TournamentStage.Unknown].includes(currentStageByLeagueId.value)) {
                selectedKnockoutStage.value = currentStageByLeagueId.value;
            } else {
                selectedKnockoutStage.value = knockoutStages.value[0].stage;
            }
        }
    });

    function updateSelectedKnockoutStage(stage: TournamentStage) {
        selectedKnockoutStage.value = stage;
    }
</script>

<style lang="scss" scoped>
    @import "@/components/tournamentCenter/Standings";
</style>
